import { useEffect } from "react";

// ─── PALETTE (shared with landing page) ───
const C = {
  night: "#0D1117", deep: "#161B22", surface: "#1C2128",
  border: "#30363D", muted: "#8B949E", text: "#C9D1D9",
  bright: "#E6EDF3", accent: "#D4A574",
  accentGlow: "rgba(212,165,116,0.15)", pulse: "#4A9EBF",
};

const Section = ({ children, style = {} }) => (
  <section style={{ padding: "32px 24px", maxWidth: 680, margin: "0 auto", ...style }}>{children}</section>
);

const Divider = () => (
  <div style={{ width: 60, height: 1, background: C.border, margin: "0 auto" }} />
);

const Heading = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: "clamp(24px, 4vw, 32px)", color: C.bright,
    lineHeight: 1.3, marginBottom: 20, ...style,
  }}>{children}</h2>
);

const Body = ({ children, style = {} }) => (
  <p style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: 16, lineHeight: 1.8, color: C.text, marginBottom: 18, ...style,
  }}>{children}</p>
);

const Accent = ({ children }) => (
  <span style={{ color: C.accent, fontStyle: "italic" }}>{children}</span>
);

const Bold = ({ children }) => (
  <strong style={{ color: C.bright, fontWeight: 700 }}>{children}</strong>
);

const Pullquote = ({ children }) => (
  <blockquote style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: "clamp(18px, 3vw, 24px)", fontStyle: "italic",
    color: C.accent, lineHeight: 1.5, maxWidth: 560,
    margin: "20px auto", textAlign: "center", padding: "0 24px",
  }}>{children}</blockquote>
);

const Feature = ({ title, children }) => (
  <div style={{
    marginBottom: 20, paddingLeft: 24,
    borderLeft: `2px solid ${C.pulse}`,
  }}>
    <p style={{
      fontFamily: "'DM Sans', sans-serif", fontSize: 14,
      fontWeight: 600, color: C.pulse, marginBottom: 8,
    }}>{title}</p>
    <p style={{
      fontFamily: "'Libre Baskerville', Georgia, serif",
      fontSize: 15, lineHeight: 1.8, color: C.text,
    }}>{children}</p>
  </div>
);

const Epigraph = ({ quote, attribution }) => (
  <div style={{
    maxWidth: 620, margin: "0 auto", padding: "0 24px",
  }}>
    <blockquote style={{
      fontFamily: "'Libre Baskerville', Georgia, serif",
      fontSize: 15, lineHeight: 1.8, color: C.muted,
      fontStyle: "italic", borderLeft: `2px solid ${C.border}`,
      paddingLeft: 24, marginBottom: 12,
    }}>&ldquo;{quote}&rdquo;</blockquote>
    <p style={{
      fontFamily: "'DM Sans', sans-serif", fontSize: 12,
      color: C.muted, textAlign: "right", letterSpacing: 1,
    }}>&mdash; {attribution}</p>
  </div>
);

export default function PopuliVision() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const goHome = (e) => {
    e.preventDefault();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <div style={{ background: C.night, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${C.accent}44; color: ${C.bright}; }
      `}</style>

      {/* ═══ HERO ═══ */}
      <header style={{
        minHeight: "60vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        position: "relative", padding: "80px 24px 40px",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${C.border}15 1px, transparent 1px), linear-gradient(90deg, ${C.border}15 1px, transparent 1px)`,
          backgroundSize: "60px 60px", opacity: 0.3,
        }} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "fadeInUp 1.2s ease-out" }}>
          <a href="/" onClick={goHome} style={{ textDecoration: "none" }}>
            <p style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontSize: 14, color: C.muted, letterSpacing: 4, marginBottom: 8,
            }}>THE POPULI PROJECT</p>
          </a>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 11,
            letterSpacing: 3, textTransform: "uppercase", color: C.border, marginBottom: 48,
          }}>We the People. In real time.</p>
          <h1 style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(32px, 6vw, 56px)", fontWeight: 700,
            color: C.bright, lineHeight: 1.3, marginBottom: 20,
          }}>The <span style={{ color: C.accent }}>Manifesto</span></h1>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 13,
            color: C.muted, letterSpacing: 1,
          }}>Civis &middot; April 2026</p>
        </div>
      </header>

      <Divider />

      {/* ═══ EPIGRAPH ═══ */}
      <Section style={{ maxWidth: 680 }}>
        <Epigraph
          quote="The mayor of every ward, on a question like the present, would call his ward together, take the simple yea or nay of its members, convey these to the county court, who would hand on those of all its wards to the proper general authority; and the voice of the whole people would be thus fairly, fully, and peaceably expressed, discussed, and decided by the common reason of the society."
          attribution="Thomas Jefferson, letter to Samuel Kercheval, 1816"
        />
      </Section>

      <Divider />

      {/* ═══ WHEN WERE YOU LAST COUNTED? ═══ */}
      <Section>
        <Heading>When were you last counted?</Heading>

        <Body>Not voted. Not surveyed. <Accent>Counted</Accent> &mdash; as in, someone actually wanted to know what you think about something that affects your life.</Body>

        <Body>Most of us can&rsquo;t remember. We vote when we can, and that&rsquo;s about it. One choice, every few years, that bundles a hundred opinions into a single lever. The rest of the time, nobody asks.</Body>

        <Body>Instead, we watch pundits explain what &ldquo;Americans believe&rdquo; &mdash; based on a few thousand phone calls, selected by methods nobody scrutinizes, treated as gospel. Every poll. Every headline. Every &ldquo;the public wants X.&rdquo;</Body>

        <Body style={{ borderLeft: `2px solid ${C.accent}`, paddingLeft: 24, fontStyle: "italic" }}>You think: nobody asked me. Nobody asked anyone I know.</Body>

        <Pullquote>What would it look like if we actually asked?</Pullquote>
      </Section>

      <Divider />

      {/* ═══ A DEMOCRACY BUILT FOR SLOWER TOOLS ═══ */}
      <Section>
        <Heading>A Democracy Built for Slower Tools</Heading>

        <Body>The founders of this country designed a system of extraordinary balance&mdash;checks between branches, tension between federal and state power, safeguards for individual liberty. At its core was a radical premise: that the legitimacy of government derives from the will of the people. It was a masterwork of distributed authority, built for a world where information traveled by horseback and a citizen&rsquo;s voice carried no further than a town hall.</Body>

        <Body style={{ borderLeft: `2px solid ${C.accent}`, paddingLeft: 24 }}>
          Jefferson put it plainly: <Accent>&ldquo;The basis of our governments being the opinion of the people, the very first object should be to keep that right.&rdquo;</Accent> The first object. Not a secondary concern. Not an afterthought. The foundation.
        </Body>

        <Body>That world no longer exists. Governments, corporations, and institutions now operate at digital speed. Narratives harden in hours. Decisions with national consequences are shaped by feedback loops that move faster than the public can meaningfully respond. The machinery of power has upgraded to fiber optics and artificial intelligence.</Body>

        <Pullquote>The people are still on horseback.</Pullquote>

        <Body>We still vote once every few years. We still hope that someone in a polling sample speaks for us. Social media briefly looked like the answer, but it only gave us a stage to perform on&mdash;not a way to stand and be counted. It captured what people would react to, not what they actually believed. It could start revolutions, but it could never sustain them, because it was never built to <Accent>listen</Accent>. It was built to amplify.</Body>

        <Body>What democracy is missing is not another platform for expression. It is a modern way to <Bold>call the roll</Bold>&mdash;to make the will of the people visible, verifiable, and impossible to ignore.</Body>
      </Section>

      <Divider />

      {/* ═══ WHAT CHANGES ═══ */}
      <Section>
        <Heading>What Changes</Heading>

        <Body>Abraham Lincoln understood this more clearly than anyone: <Accent>&ldquo;Public sentiment is everything. With public sentiment, nothing can fail; without it, nothing can succeed.&rdquo;</Accent> He spoke those words in 1858. They are truer now than ever&mdash;and yet we still have no reliable instrument to measure the very thing he called everything.</Body>

        <Body>Democracy was founded on a principle: that the will of the people should guide the course of the nation. But you cannot govern by the will of the people if you have no reliable way to know what that will is. When Populi makes verified public sentiment visible, the balance of power shifts.</Body>

        <Body>Politicians can no longer speak confidently for &ldquo;the people&rdquo; without evidence. Media narratives can be tested against a broader public signal. Institutions can no longer assume that silence means consent. The will of the people stops being something that politicians claim and becomes something that people demonstrate.</Body>

        <Body>Populi does not replace elections, journalism, protest, or public debate. It adds something we do not currently have: a continuous, trustworthy way to measure the will of the people&mdash;not once every four years, but every single day. Not just in one country. In the world. Because the world is probably more aligned than the loudest voices &mdash; on any given issue, in any given moment &mdash; would have us believe. That missing layer could improve everything built on top of it.</Body>
      </Section>

      <Divider />

      {/* ═══ THE MANDATE PROBLEM ═══ */}
      <Section>
        <Heading>The Mandate Problem</Heading>

        <Body>When someone wins an election, they are granted something we call a mandate &mdash; the authority to govern on behalf of the people who chose them. But a mandate is not a contract. There is no mechanism to enforce it, no instrument to measure it, and no way for the public to formally say <Accent>that&rsquo;s not what we meant</Accent> when decisions veer away from what was promised.</Body>

        <Body>Between elections, the feedback loop breaks down almost entirely. Public sentiment surfaces when something fails badly enough to make the news, or when the next campaign begins and candidates start listening again. The rest of the time, officials govern in a vacuum &mdash; informed by lobbyists, party leadership, donors, and the occasional constituent letter. Not because they are indifferent to public opinion. Because there is no reliable way to know what it is.</Body>

        <Body>Populi addresses this on two levels.</Body>

        <Body>The first is the standing question &mdash; how does the public feel about the issues that define political life? Immigration, healthcare, taxation, foreign policy. A continuous, verified record of where people actually stand, not filtered through a campaign ad or a cable segment.</Body>

        <Body>The second is the live question &mdash; Congress just passed this. The administration just decided that. Do you support it? That is a different kind of signal. It is specific, timely, and tied to an actual decision. It creates something that does not currently exist: a formal, public record of whether the people agree with what is being done in their name.</Body>

        <Body>Neither replaces elections, protest, journalism, or the courts. But together they close the gap between election cycles &mdash; turning public sentiment from something officials can claim into something citizens can demonstrate.</Body>

        <Pullquote>The people gain a way to raise their hand between elections and say: we&rsquo;re still here, we&rsquo;re paying attention, and this is where we stand.</Pullquote>
      </Section>

      <Divider />

      {/* ═══ WHY I BELIEVE THIS MUST EXIST ═══ */}
      <Section>
        <Heading>Why I Believe This Must Exist</Heading>

        <Body>In November 2011, I walked into Zuccotti Park on the one-month anniversary of Occupy Wall Street. The encampment had been cleared the night before. A couple hundred protesters remained, outnumbered by police, outnumbered by press. The entire world was watching&mdash;and there was still no mechanism for the millions of people who shared some version of that frustration to register it in a structured, credible way.</Body>

        <Body>I was living in New York at the time. I heard the conversations happening everywhere&mdash;across backgrounds, income levels, political leanings. Most people agreed something was broken. But they were never going to hold a sign in a park. Not because they didn&rsquo;t care. Because protest was not the right instrument for what they needed to do. They didn&rsquo;t need to shout. They needed to raise their hand and be counted.</Body>

        <Body>I have carried that conviction for nearly two decades. <Accent>The tools are finally here. The need has never been greater. The question is whether we will build them.</Accent></Body>
      </Section>

      <Divider />

      {/* ═══ THIS ONLY WORKS AT SCALE ═══ */}
      <Section>
        <Heading>This Only Works at Scale</Heading>

        <Pullquote>A thousand voices are interesting. A million are undeniable. A hundred million change the course of history.</Pullquote>

        <Body>That means Populi cannot be launched at people like another app. It has to be built <Accent>with</Accent> them. Every person who joins makes the data more representative. Every community that adopts it makes the platform harder to ignore. Every person who shares this with someone they trust extends the reach of a civic tool that belongs to no one and serves everyone.</Body>

        <Body>If you are a builder, help design it. If you are a researcher, help test it. If you are a journalist, help interrogate it. If you are a citizen who believes public opinion deserves better infrastructure, <Bold>add your voice early</Bold>.</Body>

        <Pullquote>We the People. In real time.</Pullquote>
      </Section>

      <Divider />

      {/* ═══ SIGNATURE ═══ */}
      <Section style={{ textAlign: "center", paddingTop: 48, paddingBottom: 16 }}>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: 22, fontStyle: "italic", color: C.accent,
          letterSpacing: 2, marginBottom: 8,
        }}>&mdash; Civis</p>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 12,
          color: C.muted, letterSpacing: 1.5, marginBottom: 16,
        }}>APRIL 2026</p>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 13,
          fontStyle: "italic", color: C.muted, letterSpacing: 0.5,
        }}>
          <span style={{ color: C.accent }}>Civis</span> &mdash; Latin for <em>citizen</em>.
        </p>
      </Section>

      {/* ═══ AI ACKNOWLEDGMENT ═══ */}
      <Section style={{ maxWidth: 620, paddingTop: 24, paddingBottom: 48 }}>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: 13, lineHeight: 1.8, color: C.muted,
          fontStyle: "italic", textAlign: "center",
          borderTop: `1px solid ${C.border}`, paddingTop: 24,
        }}>
          A note on authorship: I have carried this idea for nearly twenty years. The conviction, the argument, and the stories are mine. The prose, the site, and the shape of this document were developed in conversation with <span style={{ color: C.accent }}>Claude</span>, an AI assistant by Anthropic. I mention this because transparency matters to any project built on trust.
        </p>
      </Section>

      {/* ═══ FOOTER ═══ */}
      <Section style={{ textAlign: "center", paddingBottom: 80 }}>
        <Divider />
        <div style={{ marginTop: 48 }}>
          <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16, color: C.muted, letterSpacing: 3 }}>THE POPULI PROJECT</p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: C.border, marginTop: 8, letterSpacing: 2 }}>
            WE THE PEOPLE. IN REAL TIME.
          </p>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted,
            marginTop: 24, letterSpacing: 1,
          }}>populi.one</p>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 14,
            fontStyle: "italic", color: C.muted, marginTop: 32, maxWidth: 420,
            marginLeft: "auto", marginRight: "auto", lineHeight: 1.6,
          }}>Share this with someone who should be part of building it.</p>
          <a href="/" onClick={goHome}
            style={{
              display: "inline-block", marginTop: 40, padding: "12px 32px",
              fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
              letterSpacing: 1.5, textTransform: "uppercase", color: C.night,
              background: C.accent, border: "none", borderRadius: 4,
              textDecoration: "none", cursor: "pointer",
            }}>Back to Home</a>
        </div>
      </Section>
    </div>
  );
}
